import React, { useEffect, useState } from 'react';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import Typography from '@mui/material/Typography';
import InputField from '../../../../../FormControl/input/InputField';
import Dialog from '../../../../../dialog/Dialog';
import useSSO from '../../../../../../hooks/setting/useSSO';
import { Alert, Divider, MenuItem, Select } from '@mui/material';
import { SelectFieldServiceSSO } from '../../../../../FormControl/SelectCustom/SelectFieldServiceSSO';
import TextCopyClipboard from '../../../../../text/TextCopyClipboard';
import { useSelector } from 'react-redux';
import { Switch } from '@mui/material';

const SSODialog = ({ show, onClose, type, data = {} }) => {
  const { domain } = useSelector((state) => state?.tenant?.tenant || {});
  const redirectUri = `https://${domain}/login`;
  const { errors: errorsData, createSSO, updateSSO, deleteSSO } = useSSO();
  const [form, setInputForm] = useState({});
  const [errors, setInputErrors] = useState(errorsData);
  const [loading, toggleLoading] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'service') {
      setInputForm({
        ...form,
        [name]: value,
        tenant: '',
        clientId: '',
        domain: '',
        issuer: ''
      });
      setInputErrors({
        ...errors,
        [name]: null,
        tenant: null,
        clientId: null,
        issuer: null,
        domain: null
      });
      return;
    }
    setInputForm({ ...form, [name]: value });
    setInputErrors({ ...errors, [name]: null });
  };

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type)) {
      setInputErrors({ message: 'Invalid type' });
      return;
    }
    e.preventDefault();
    toggleLoading(true);
    try {
      let result = { error: false };
      if (type === 'create') {
        result = await createSSO(form);
      }
      if (type === 'edit') {
        result = await updateSSO(form._id, form);
      }
      if (type === 'delete') {
        result = await deleteSSO(form._id);
      }
      if (result.error) {
        setInputErrors(result);
      } else {
        onClose();
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    setInputErrors(errorsData);
  }, [errorsData]);

  useEffect(() => {
    if (data) {
      setInputForm({
        ...data,
        ...data.config
      });
    }
  }, [data]);

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  );

  return (
    <Dialog id={`${type}-sso`} show={show} title={`${type} service SSO`} maxWidth="md" onClose={onClose} actions={Actions}>
      {type === 'delete' ? (
        <Typography>
          Are you sure you want to delete the service: <b>{form.name}</b>?
        </Typography>
      ) : (
        <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Typography
              variant='inherit'
              className='font-bold pl-6'
              color='primary'
              align='right'
              component='p'
            >
              Enabled
            </Typography>
            <div className='col-span-3'>
              <Switch
                id='isEnabled'
                color='primary'
                name='isEnabled'
                checked={form?.isEnabled || false}
                onChange={(e) => onChange({ target: { name: 'isEnabled', value: e.target.checked } })}
                readOnly={type === 'preview'}
              />
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Service
            </Typography>
            <div className='col-span-3'>
              <SelectFieldServiceSSO
                id='service'
                name='service'
                value={form.service}
                error={errors?.service}
                disabled={type !== 'create'}
                isPreview={type !== 'create'}
                onChange={onChange}
              />
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Name
            </Typography>
            <div className='col-span-3'>
              {type === 'preview' ? (
                <Typography>{form.name}</Typography>
              ) : (
                <InputField
                  id='name'
                  name='name'
                  value={form.name}
                  error={errors?.name}
                  helperText={errors?.name}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Label button
            </Typography>
            <div className='col-span-3'>
              {type === 'preview' ? (
                <Typography>{form.label}</Typography>
              ) : (
                <InputField
                  id='label'
                  name='label'
                  value={form.label}
                  error={errors?.label}
                  helperText={errors?.label}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            {form.service === 'microsoft' && (
              <>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Tenant
                </Typography>
                <div className='col-span-3'>
                  {type === 'preview' ? (
                    <Typography className='ext-nowrap text-ellipsis overflow-hidden'>
                      {form.tenant}
                    </Typography>
                  ) : (
                    <InputField
                      id='tenant'
                      name='tenant'
                      value={form.tenant}
                      error={errors?.tenant}
                      helperText={errors?.tenant}
                      onChange={onChange}
                      fullWidth
                    />
                  )}
                </div>
              </>
            )}
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Client ID
            </Typography>
            <div className='col-span-3'>
              {type === 'preview' ? (
                <Typography className='text-nowrap text-ellipsis overflow-hidden'>{form.clientId}</Typography>
              ) : (
                <InputField
                  id='clientId'
                  name='clientId'
                  value={form.clientId}
                  error={errors?.clientId}
                  helperText={errors?.clientId}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            {form.service === 'microsoft' && (
              <>
                <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
                  Auth Flow
                </Typography>
                <div className='col-span-3'>
                  {type === 'preview' ? (
                    <Typography>{form.authFlow === 'authorization_code_pkce' ? 'PKCE (secure)' : 'Implicit (legacy)'}</Typography>
                  ) : (
                    <Select
                      id='authFlow'
                      name='authFlow'
                      value={form.authFlow || 'implicit'}
                      onChange={onChange}
                      size='small'
                      fullWidth
                    >
                      <MenuItem value='implicit'>Implicit (legacy)</MenuItem>
                      <MenuItem value='authorization_code_pkce'>PKCE (secure)</MenuItem>
                    </Select>
                  )}
                </div>
              </>
            )}
            {form.service === 'cloudwork' && (
              <>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Issuer
                </Typography>
                <div className='col-span-3'>
                  {type === 'preview' ? (
                    <Typography className='ext-nowrap text-ellipsis overflow-hidden'>
                      {form.issuer}
                    </Typography>
                  ) : (
                    <InputField
                      id='issuer'
                      name='issuer'
                      value={form.issuer}
                      error={errors?.issuer}
                      helperText={errors?.issuer}
                      onChange={onChange}
                      fullWidth
                    />
                  )}
                </div>
              </>
            )}
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Domain
            </Typography>
            <div className='col-span-3'>
              {type === 'preview' ? (
                <Typography>{form.domain}</Typography>
              ) : (
                <InputField
                  id='domain'
                  name='domain'
                  value={form.domain}
                  error={errors?.domain}
                  helperText={errors?.domain}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            {form._id && (
              <>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Redirect URI
                </Typography>
                <div className='col-span-3'>
                  <TextCopyClipboard text={redirectUri} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default SSODialog;
